// src/components/PortalAfiliado/CadastroAlunoForm.tsx
// UPDATED: 2026-03-27 — v3.7
// ✅ Upload foto e PATCH usam fetch nativo (evita interceptor 401 do apiClient)
// ✅ Erros de upload/patch são silenciosos — não bloqueiam o cadastro
// ✅ Datas convertidas para ISO 8601
// ✅ Sem redirect após cadastro

import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  UserPlus, User, Mail, Phone, CreditCard, Calendar, MapPin,
  CheckCircle, AlertCircle, Camera, BookOpen, Clock, Sparkles, Upload, X,
} from 'lucide-react';
import { studentService } from '../../services/studentService';
import { API_BASE, getToken } from '../../utils/apiConfig';

interface RootState {
  auth: { user?: { affiliateId?: string; schoolName?: string } };
}

interface FormData {
  fullName: string; email: string; cpf: string; cin: string;
  phone: string; whatsapp: string; birthdate: string;
  address: string; city: string; state: string; postalCode: string;
  courseName: string; courseWorkload: string;
  courseStartDate: string; courseEndDate: string;
}
type FormErrors = Partial<Record<keyof FormData, string>>;

const ib = 'w-full py-2.5 border rounded-xl text-sm text-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent';
const ic = (err?: boolean) => `${ib} px-4 ${err ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'}`;
const ii = (err?: boolean) => `${ib} pl-10 pr-4 ${err ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'}`;
const ufs = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const toISO = (d: string) => d ? new Date(d).toISOString() : undefined;
const VAZIO: FormData = { fullName:'',email:'',cpf:'',cin:'',phone:'',whatsapp:'',birthdate:'',address:'',city:'',state:'',postalCode:'',courseName:'',courseWorkload:'',courseStartDate:'',courseEndDate:'' };

const Field: React.FC<{ label:string; required?:boolean; error?:string; children:React.ReactNode }> = ({ label,required,error,children }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
      {label}{required && <span className="text-gold-600 ml-1">*</span>}
    </label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0"/>{error}</p>}
  </div>
);

const Sec: React.FC<{ icon:React.ReactNode; label:string }> = ({ icon,label }) => (
  <div className="flex items-center gap-2.5 mb-5">
    <div className="w-7 h-7 rounded-lg bg-gold-400/10 flex items-center justify-center"><span className="text-gold-600">{icon}</span></div>
    <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">{label}</h2>
    <div className="flex-1 h-px bg-gray-100 ml-1"/>
  </div>
);

// ── Upload via fetch nativo (sem interceptor) ─────────────────────────────────
const uploadFotoViaDocuments = async (
  fotoFile: File,
  affiliateId: string,
): Promise<string | null> => {
  try {
    const fd = new FormData();
    fd.append('file', fotoFile);

    const res = await fetch(
      `${API_BASE}/documents/public-upload?affiliateId=${encodeURIComponent(affiliateId)}&documentType=foto&description=Foto%20do%20aluno`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
        // NÃO setar Content-Type — browser seta multipart+boundary automaticamente
      },
    );

    if (!res.ok) {
      console.warn('[CadastroAluno] upload foto status:', res.status);
      return null;
    }

    const data = await res.json();
    return data.url ?? data.fileUrl ?? data.path ?? data.signedUrl ?? null;
  } catch (err: any) {
    console.warn('[CadastroAluno] upload foto erro:', err?.message);
    return null;
  }
};

// ── PATCH via fetch nativo (sem interceptor — evita redirect 401) ─────────────
const patchStudentPhoto = async (studentId: string, photoUrl: string): Promise<void> => {
  try {
    const res = await fetch(`${API_BASE}/students/${studentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ photoUrl }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      console.warn('[CadastroAluno] PATCH photoUrl falhou:', res.status, body);
    }
  } catch (err: any) {
    console.warn('[CadastroAluno] PATCH photoUrl erro:', err?.message);
  }
};

// ── Componente ────────────────────────────────────────────────────────────────
const CadastroAlunoForm: React.FC = () => {
  const affiliateId = useSelector((s: RootState) => s.auth.user?.affiliateId ?? '');
  const schoolName  = useSelector((s: RootState) => s.auth.user?.schoolName  ?? '');

  const [form, setForm]               = useState<FormData>(VAZIO);
  const [fotoFile, setFotoFile]       = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [enrolledId, setEnrolledId]   = useState<string | null>(null);
  const [loading, setLoading]         = useState(false);
  const [step, setStep]               = useState<'idle'|'enrolling'|'uploading'>('idle');
  const [apiError, setApiError]       = useState<string | null>(null);
  const [errs, setErrs]               = useState<FormErrors>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errs[name as keyof FormData]) setErrs(p => ({ ...p, [name]: undefined }));
    setApiError(null);
  };

  const handleFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setApiError('Selecione apenas imagens'); return; }
    if (f.size > 5 * 1024 * 1024)     { setApiError('Máximo 5MB'); return; }
    setFotoFile(f);
    const r = new FileReader();
    r.onload = ev => setFotoPreview(ev.target?.result as string);
    r.readAsDataURL(f);
    setApiError(null);
  };

  const validate = () => {
    const e: FormErrors = {};
    if (!form.fullName.trim())   e.fullName       = 'Obrigatório';
    if (!form.email.trim())      e.email          = 'Obrigatório';
    if (!form.cpf.trim() && !form.cin.trim()) e.cpf = 'Informe CPF ou CIN';
    if (!form.birthdate)         e.birthdate      = 'Obrigatório';
    if (!form.phone.trim())      e.phone          = 'Obrigatório';
    if (!form.courseName.trim()) e.courseName     = 'Obrigatório';
    if (!form.courseWorkload)    e.courseWorkload  = 'Obrigatório';
    if (!form.courseStartDate)   e.courseStartDate = 'Obrigatório';
    if (!form.courseEndDate)     e.courseEndDate   = 'Obrigatório';
    if (!affiliateId) setApiError('Sessão inválida — recarregue a página.');
    setErrs(e);
    return Object.keys(e).length === 0 && !!affiliateId;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true); setApiError(null);

    try {
      // PASSO 1: Matricula o aluno
      setStep('enrolling');
      const result = await studentService.enroll({
        affiliateId,
        fullName:        form.fullName.trim(),
        email:           form.email.trim(),
        cpf:             form.cpf.trim()        || undefined,
        cin:             form.cin.trim()        || undefined,
        phone:           form.phone.trim()      || undefined,
        whatsapp:        form.whatsapp.trim()   || undefined,
        birthdate:       toISO(form.birthdate),
        address:         form.address.trim()    || undefined,
        city:            form.city.trim()       || undefined,
        state:           form.state             || undefined,
        postalCode:      form.postalCode.trim() || undefined,
        courseName:      form.courseName.trim() || undefined,
        courseWorkload:  form.courseWorkload ? Number(form.courseWorkload) : undefined,
        courseStartDate: toISO(form.courseStartDate),
        courseEndDate:   toISO(form.courseEndDate),
      });

      // PASSO 2 + 3: Upload foto e PATCH — via fetch nativo (sem interceptor 401)
      if (fotoFile && result.id) {
        setStep('uploading');
        const photoUrl = await uploadFotoViaDocuments(fotoFile, affiliateId);
        if (photoUrl) {
          await patchStudentPhoto(result.id, photoUrl);
        }
      }

      // Sucesso — permanece no portal
      setEnrolledId(result.systemId);
      setForm(VAZIO);
      setFotoFile(null);
      setFotoPreview(null);
      if (fileRef.current) fileRef.current.value = '';

    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Erro ao cadastrar aluno';
      setApiError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setLoading(false);
      setStep('idle');
    }
  };

  // ── Tela de sucesso ──────────────────────────────────────────
  if (enrolledId) return (
    <div className="space-y-6 max-w-lg mx-auto pt-6">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-8 h-8 text-emerald-500"/>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Aluno cadastrado!</h2>
        <p className="text-sm text-gray-500 mb-6">O aluno recebeu acesso ao portal com senha padrão.</p>
        <div className="flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-gold-50 border border-gold-200 mb-6">
          <Sparkles className="w-5 h-5 text-gold-500 flex-shrink-0"/>
          <div className="text-left">
            <p className="text-[10px] font-semibold text-gold-600 uppercase tracking-wider mb-0.5">ID do Aluno</p>
            <p className="text-lg font-bold text-cosmos-900 font-mono tracking-widest">{enrolledId}</p>
          </div>
        </div>
        <div className="space-y-2 text-xs text-gray-400 mb-6">
          <p>• Anote o ID — ele identifica o aluno na federação.</p>
          <p>• O aluno pode fazer login com o e-mail e senha padrão.</p>
        </div>
        <button
          onClick={() => { setEnrolledId(null); setApiError(null); }}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium text-cosmos-900 mx-auto"
          style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}
        >
          <UserPlus className="w-4 h-4"/> Cadastrar outro aluno
        </button>
      </div>
    </div>
  );

  // ── Formulário ───────────────────────────────────────────────
  const stepLabel = step === 'enrolling' ? 'Cadastrando…' : step === 'uploading' ? 'Enviando foto…' : '';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3.5">
        <div className="relative flex-shrink-0">
          <div className="absolute inset-0 bg-gold-400/20 blur-[10px] rounded-full"/>
          <img src="/icons/gere.png" alt="" className="relative w-11 h-11 object-contain drop-shadow"/>
        </div>
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">Cadastrar Novo Aluno</h1>
          <p className="text-xs text-gray-400 mt-1">{schoolName ? `Escola: ${schoolName}` : 'Preencha os dados do aluno'}</p>
        </div>
      </div>

      {apiError && (
        <div className="flex items-start gap-3 px-5 py-4 rounded-2xl border border-red-200 bg-red-50">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"/>
          <p className="text-sm text-red-700">{apiError}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden max-w-4xl">
        <form onSubmit={handleSubmit} noValidate>

          {/* DADOS PESSOAIS */}
          <div className="p-6 sm:p-8 border-b border-gray-50">
            <Sec icon={<User className="w-3.5 h-3.5"/>} label="Dados Pessoais"/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Field label="Nome completo" required error={errs.fullName}>
                  <div className="relative"><User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                    <input type="text" name="fullName" value={form.fullName} onChange={handleChange} placeholder="João Silva Santos" className={ii(!!errs.fullName)}/></div>
                </Field>
              </div>
              <Field label="E-mail" required error={errs.email}>
                <div className="relative"><Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="joao@exemplo.com" className={ii(!!errs.email)}/></div>
              </Field>
              <Field label="Contato" required error={errs.phone}>
                <div className="relative"><Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="(11) 99999-9999" className={ii(!!errs.phone)}/></div>
              </Field>
              <Field label="WhatsApp">
                <div className="relative"><Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="tel" name="whatsapp" value={form.whatsapp} onChange={handleChange} placeholder="(11) 99999-9999" className={ii()}/></div>
              </Field>
              <Field label="Data de Nascimento" required error={errs.birthdate}>
                <div className="relative"><Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="date" name="birthdate" value={form.birthdate} onChange={handleChange} className={ii(!!errs.birthdate)}/></div>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
              <Field label="CPF" error={errs.cpf}>
                <div className="relative"><CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="text" name="cpf" value={form.cpf} onChange={handleChange} placeholder="000.000.000-00" maxLength={14} className={ii(!!errs.cpf)}/></div>
              </Field>
              <Field label="CIN (alternativa ao CPF)">
                <div className="relative"><CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="text" name="cin" value={form.cin} onChange={handleChange} placeholder="Número do CIN" className={ii()}/></div>
              </Field>
              <p className="sm:col-span-2 text-[11px] text-gray-400 -mt-2">Informe CPF <em>ou</em> CIN — ao menos um é obrigatório.</p>
            </div>
          </div>

          {/* CURSO */}
          <div className="p-6 sm:p-8 border-b border-gray-50">
            <Sec icon={<BookOpen className="w-3.5 h-3.5"/>} label="Curso Realizado"/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Field label="Curso realizado" required error={errs.courseName}>
                  <div className="relative"><BookOpen className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                    <input type="text" name="courseName" value={form.courseName} onChange={handleChange} placeholder="Ex: Cosmetologia Avançada" className={ii(!!errs.courseName)}/></div>
                </Field>
              </div>
              <Field label="Carga horária (horas)" required error={errs.courseWorkload}>
                <div className="relative"><Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="number" name="courseWorkload" value={form.courseWorkload} onChange={handleChange} placeholder="120" min={1} className={ii(!!errs.courseWorkload)}/></div>
              </Field>
              <Field label="Data de Início" required error={errs.courseStartDate}>
                <div className="relative"><Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="date" name="courseStartDate" value={form.courseStartDate} onChange={handleChange} className={ii(!!errs.courseStartDate)}/></div>
              </Field>
              <Field label="Data de Término" required error={errs.courseEndDate}>
                <div className="relative"><Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="date" name="courseEndDate" value={form.courseEndDate} onChange={handleChange} className={ii(!!errs.courseEndDate)}/></div>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Escola credenciada">
                  <input type="text" value={schoolName || 'Sua escola'} readOnly className={`${ic()} bg-gray-50 text-gray-500 cursor-not-allowed`}/>
                  <p className="mt-1 text-[11px] text-gray-400">A logomarca será inserida no diploma do aluno.</p>
                </Field>
              </div>
            </div>
          </div>

          {/* ENDEREÇO */}
          <div className="p-6 sm:p-8 border-b border-gray-50">
            <Sec icon={<MapPin className="w-3.5 h-3.5"/>} label="Endereço"/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="CEP">
                <input type="text" name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="00000-000" maxLength={9} className={ic()}/>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Endereço">
                  <input type="text" name="address" value={form.address} onChange={handleChange} placeholder="Rua, número, complemento" className={ic()}/>
                </Field>
              </div>
              <Field label="Cidade">
                <input type="text" name="city" value={form.city} onChange={handleChange} placeholder="São Paulo" className={ic()}/>
              </Field>
              <Field label="Estado">
                <select name="state" value={form.state} onChange={handleChange} className={ic()}>
                  <option value="">Selecione</option>
                  {ufs.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </Field>
            </div>
          </div>

          {/* FOTO */}
          <div className="p-6 sm:p-8">
            <Sec icon={<Camera className="w-3.5 h-3.5"/>} label="Foto do Aluno"/>
            <Field label="Foto para carteira digital (opcional)">
              {fotoPreview ? (
                <div className="flex items-center gap-4 p-3 border-2 border-gold-200 bg-gold-50/30 rounded-xl">
                  <img src={fotoPreview} alt="Preview" className="w-20 h-20 rounded-xl object-cover border-2 border-gold-300 flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-700 truncate">{fotoFile?.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{fotoFile ? `${(fotoFile.size/1024).toFixed(0)} KB` : ''}</p>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/>Será enviada com o cadastro</p>
                  </div>
                  <button type="button" onClick={() => { setFotoFile(null); setFotoPreview(null); if(fileRef.current) fileRef.current.value=''; }}
                    className="p-2 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 flex-shrink-0">
                    <X className="w-4 h-4"/>
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-gold-400 hover:bg-gold-50/30 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-gold-100 flex items-center justify-center transition-colors">
                    <Upload className="w-5 h-5 text-gray-400 group-hover:text-gold-500 transition-colors"/>
                  </div>
                  <p className="text-sm font-medium text-gray-600 group-hover:text-gold-700">Clique para selecionar</p>
                  <p className="text-xs text-gray-400">JPG, PNG ou WEBP — máx. 5MB</p>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFoto} className="hidden"/>
                </label>
              )}
              <p className="mt-1.5 text-[11px] text-gray-400">A foto será usada na carteira digital do aluno.</p>
            </Field>
          </div>

          {/* AÇÕES */}
          <div className="px-6 sm:px-8 pb-8 flex items-center justify-end gap-3 border-t border-gray-50 pt-6">
            <button type="button"
              onClick={() => { setForm(VAZIO); setFotoFile(null); setFotoPreview(null); setErrs({}); setApiError(null); }}
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
              Limpar
            </button>
            <button type="submit" disabled={loading}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium text-cosmos-900 disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
              {loading
                ? <><div className="w-4 h-4 border-2 border-cosmos-900/30 border-t-cosmos-900 rounded-full animate-spin"/>{stepLabel}</>
                : <><UserPlus className="w-4 h-4"/>Cadastrar Aluno</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroAlunoForm;